import React, { Children, useEffect, useRef, useState, } from 'react';
import { Wrapper } from "@googlemaps/react-wrapper";
import { getPins } from './api/location';
import CustomMarker from './CustomMarker';
import { Root, createRoot } from 'react-dom/client';
import { LocationData } from './LocationTypes';
import LocationInfoContent from './LocationInfoContent';

type Location = {
    latitude: number;
    longitude: number;
};

interface ILocationCoordetnation {
    northEast: {
        longitude: number,
        latitude: number,
    },
    southWest: {
        longitude: number,
        latitude: number,
    },
}

const getBoundaries = (map: google.maps.Map): ILocationCoordetnation | null => {
    try {
        if (!map) {
            return null;
        }
        const boundaries = map.getBounds();
        if (!boundaries) {
            return null;
        }


        const lonMin = boundaries.getSouthWest().lng();
        const lonMax = boundaries.getNorthEast().lng();
        const latMin = boundaries.getSouthWest().lat();
        const latMax = boundaries.getNorthEast().lat();

        const newLonMin = lonMin - (lonMax - lonMin) / 2;
        const newLonMax = lonMax + (lonMax - lonMin) / 2;
        const newLatMin = latMin - (latMax - latMin) / 2;
        const newLatMax = latMax + (latMax - latMin) / 2;

        return {
            northEast: {
                longitude: newLonMax,
                latitude: newLatMax,
            },
            southWest: {
                longitude: newLonMin,
                latitude: newLatMin,
            },
        };
    } catch (e) {
        console.error(e)
        return null;
    }
};

function GoogleMap({ center }: { center: { lat: number, lng: number } }) {
    const [map, setMap] = React.useState(google.maps.Map.prototype);
    const _ref = useRef<HTMLDivElement>(null);
    const [locations, setLocations] = useState<LocationData[]>([]);
    const [selectedLocation, setSelectedLocation] = useState<LocationData | undefined>(undefined)
    const [mapCenterPosition, setMapCenterPosition] = useState<ILocationCoordetnation | null>(null);
    let zoomDebounceTimeout: NodeJS.Timeout | null;


    useEffect(() => {
        if (!_ref.current) {
            console.log(_ref.current)
            return;
        }
        const map = new window.google.maps.Map(_ref.current, {
            center: center,
            zoom: 14,
            mapId: '2cd91561ac296a6d'
        });
        setMap(map);

        google.maps.event.addListener(map, 'dragend', function () {
            setMapCenterPosition(getBoundaries(map))
        });

        google.maps.event.addListener(map, 'zoom_changed', function () {
            if (zoomDebounceTimeout !== null) {
                clearTimeout(zoomDebounceTimeout);
                zoomDebounceTimeout = null;
            }
            zoomDebounceTimeout = setTimeout(() => {
                setMapCenterPosition(getBoundaries(map));
            }, 500);
        });

    }, [])
    useEffect(() => {
        setTimeout(() => {
            const boundaries = getBoundaries(map);
            if (boundaries) {
                loadLocations(boundaries);
            }
        }, 1000);

    }, [map, mapCenterPosition])

    const loadLocations = async (
        coordinate: ILocationCoordetnation

    ) => {

        const lonMin = coordinate.southWest.longitude;
        const lonMax = coordinate.northEast.longitude;
        const latMin = coordinate.southWest.latitude;
        const latMax = coordinate.northEast.latitude;

        try {
            const response = await getPins(
                { lonMin, lonMax, latMin, latMax, groupPins: false },
                {},
            );
            if (!response) {
                return;
            }
            const { result } = response.data;
            const newLocations: LocationData[] = result;
            if ((map as any).markers?.length > 0) {
                const copyOfMarkers = [...(map as any).markers];
                for (let i = 0; i < copyOfMarkers.length; i += 1) {
                    const marker: google.maps.marker.AdvancedMarkerElement = copyOfMarkers[i];
                    if (newLocations.findIndex(m => m.longitude === marker.position?.lng && m.latitude === marker.position?.lat) === -1) {
                        marker.map = null;
                        (map as any).markers.splice(i, 1);
                    }
                }

            }
            setLocations(newLocations);
        } catch (e) {
        }
    };

    return <>
        <div ref={_ref} id="map" style={{ height: '100vh' }} />
        {
            map && <>
                <LocationMarkers map={map} locations={locations} onLocationSelection={(id: number) => {
                    if (id === -1) {
                        setSelectedLocation(undefined)
                        return;
                    }
                    const t = locations.filter(l => l.id === id);
                    setSelectedLocation(t[0]);
                }} />
                <LocationInfoContent location={selectedLocation} />
            </>
        }
    </>
}

function Marker({ map, children, latitude, longitude, onclick }: { map: google.maps.Map, children: any, latitude: number, longitude: number, onclick: () => void }) {
    const markerRef = useRef<google.maps.marker.AdvancedMarkerElement>();
    const rootRef = useRef<Root>();

    useEffect(() => {
        if (!rootRef.current) {
            const container = document.createElement('div');
            rootRef.current = createRoot(container);
            const advancedMarkerView = new google.maps.marker.AdvancedMarkerElement({ map: map, content: container, position: { lat: latitude, lng: longitude } });
            markerRef.current = advancedMarkerView;
            if (!(map as any).markers) {
                (map as any).markers = [];
            }
            advancedMarkerView.addListener('click', () => {
                const _z = map.getZoom();
                if (_z) {
                    map.setZoom(_z < 16 ? 16 : _z + 1);
                    map.setCenter(advancedMarkerView.position as google.maps.LatLng);
                }
                onclick();
            });
            (map as any).markers.push(advancedMarkerView);
        }

    }, []);

    useEffect(() => {
        rootRef.current?.render(children);

    }, [map, children])

    return <></>
}

function LocationMarkers({ map, locations, onLocationSelection }: { map: google.maps.Map, locations: LocationData[], onLocationSelection: (id: number) => void }) {

    const [highlight, setHighlight] = useState();
    const [markers, setMarkers] = useState<JSX.Element[]>([]);

    const elements = locations.map(location => (
        <Marker
            onclick={() => {
                if (location.isCluster) {
                    onLocationSelection(-1);
                    return
                }
                onLocationSelection(location.id);
            }}
            key={location.isCluster ? location.zoomDelta : location.id} map={map}
            latitude={location.latitude}
            longitude={location.longitude} >
            <CustomMarker
                onHover={() => { console.log('enter') }}
                location={location}
                highlight={highlight === location.id}
            />
        </Marker>
    ))

    useEffect(() => {
        setMarkers(elements);
    }, [locations])

    return <>
        {elements}
    </>
}

export default function LocationMap({ locationDetails }: { locationDetails: { lat: number, lng: number } }) {

    const renderMap = () => {
        return (
            <Wrapper apiKey='AIzaSyC2w0NtRwp4FzSxTHnBHHfz7eGPgyNRjvI' libraries={["marker"]} version='beta'>
                <GoogleMap center={locationDetails} />
            </Wrapper>
        );
    };

    return renderMap();
};